// ./application-steps/StepContent5.js

import React, { useEffect, useState, useContext, memo } from "react";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import FormInput from "../../helpers/FormInput";
import { phoneRegex, emailRegex } from "../../helpers/validationHelpers";

import {
  useGetDasStep5Query,
  useAddDasStep5Mutation,
  useDeleteDasStep5Mutation,
} from "../../../services/das-app";
import { NotificationContext } from "../../../context/NotificationContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

function StepContent5({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
  appID,
}) {
  const { addNotification } = useContext(NotificationContext);
  const { data, isLoading, isError, refetch } = useGetDasStep5Query(appID, {
    skip: !appID,
  });
  const [addDasStep5] = useAddDasStep5Mutation();
  const [deleteDasStep5] = useDeleteDasStep5Mutation();

  const [openModal, setOpenModal] = useState(false);
  const [newReference, setNewReference] = useState({
    Name: "",
    Position: "",
    Phone: "",
    Email: "",
  });

  useEffect(() => {
    if (data && data.success && data.data) {
      // If needed, process data here
      // Currently, data is managed via the query and displayed directly
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleOpenModal = (type) => {
    setNewReference({
      Name: "",
      Position: "",
      Phone: "",
      Email: "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewReference({
      Name: "",
      Position: "",
      Phone: "",
      Email: "",
    });
  };

  const handleOldChange = (field) => (e) => {
    setNewReference((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSaveReference = async () => {
    const { Name, Position, Phone, Email } = newReference;

    if (
      Name.trim() === "" ||
      Position.trim() === "" ||
      Phone.trim() === "" ||
      Email.trim() === ""
    ) {
      addNotification("All fields are required.", "error");
      return;
    }

    if (!phoneRegex.test(Phone.trim())) {
      addNotification("Please enter a valid phone number.", "error");
      return;
    }

    if (!emailRegex.test(Email.trim())) {
      addNotification("Please enter a valid email address.", "error");
      return;
    }

    try {
      await addDasStep5({
        appID,
        data: {
          Name: Name.trim(),
          Position: Position.trim(),
          Phone: Phone.trim(),
          Email: Email.trim(),
        },
      }).unwrap();
      addNotification("Reference added successfully.", "success");
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error("Error adding Reference:", error);
      addNotification("Failed to add Reference. Please try again.");
    }
  };

  const handleDeleteReference = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this reference?"
    );
    if (!confirmDelete) return;

    try {
      await deleteDasStep5({ appID, id }).unwrap();
      addNotification("Reference deleted successfully.", "success");
      refetch();
    } catch (error) {
      console.error("Error deleting reference:", error);
      addNotification("Failed to delete reference. Please try again.");
    }
  };

  const renderTable = (type) => {
    const references = data?.data || [];

    return (
      <TableContainer component={Paper} sx={{ marginBottom: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Position</strong>
              </TableCell>
              <TableCell>
                <strong>Phone Number</strong>
              </TableCell>
              <TableCell>
                <strong>Email</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {references?.map((ref) => (
              <TableRow key={ref?.Id}>
                <TableCell>{ref?.Name}</TableCell>
                <TableCell>{ref?.Position}</TableCell>
                <TableCell>{ref?.Phone}</TableCell>
                <TableCell>{ref?.Email}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="delete"
                    color="error"
                    onClick={() => handleDeleteReference(ref?.Id)}
                    disabled={isReadOnly}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {references.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No references found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div className="form-group">
      <h3 className="bold-text">
        Teacher And/Or Community Reference(s) For Your Area Of Interest:
      </h3>

      <Typography variant="body1" gutterBottom>
        Please list a minimum of two teachers and/or community members who can
        attest to your achievements in your chosen area of interest and will be
        providing a written reference on your behalf.
      </Typography>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : isError ? (
        <Typography color="error">Failed to load references.</Typography>
      ) : (
        <>
          {renderTable()}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, mb: 1 }}
          >
            * Please ensure at least two references are added.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
            sx={{ mt: 2 }}
            disabled={isReadOnly}
          >
            Add Reference
          </Button>
        </>
      )}

      {/* Modal for Adding Reference */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-reference-modal-title"
        aria-describedby="add-reference-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="add-reference-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Add Reference
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInput
                label="Name"
                id="Name"
                value={newReference.Name}
                onChange={handleOldChange("Name")}
                required
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Position"
                id="Position"
                value={newReference.Position}
                onChange={handleOldChange("Position")}
                required
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Phone Number"
                id="Phone"
                value={newReference.Phone}
                onChange={handleOldChange("Phone")}
                required
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label="Email"
                id="Email"
                type="email"
                value={newReference.Email}
                onChange={handleOldChange("Email")}
                required
                disabled={isReadOnly}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                onClick={handleCloseModal}
                color="secondary"
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveReference}
                variant="contained"
                color="primary"
                disabled={isReadOnly}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
}

export default memo(StepContent5);
